<script lang="ts" setup>
import { useDocument } from '@/composition'
import { editor } from '@/helper/const'
import { useBuilderStore } from '@/store/builder'
import { IElement } from '@gohighlevel/ghl-proposals-common'
import { PropType, computed, ref, watch } from 'vue'
import RichTextTableContent from '../RichTextTableContent.vue'
import { useAppStore } from '@/store/app'

const props = defineProps({
  element: {
    type: Object as PropType<IElement>,
    required: true,
  },
})
const { loadingCustomValues, documentVersion } = useDocument()
const store = useBuilderStore()
const appStore = useAppStore()
const flatCustomValues = computed(() => ({
  ...store.flatCustomValues,
  ...appStore.flatDocumentVariables,
}))
const refreshCount = ref(0)

const isEditable = computed(() => store.isDocumentEditable)

watch(
  () => [flatCustomValues.value, loadingCustomValues.value],
  () => {
    refreshCount.value = 1 + refreshCount.value
  }
)

const customStyleForTextParent = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return {
    marginTop: styles?.marginTop,
    marginBottom: styles?.marginBottom,
    marginLeft: styles?.marginLeft,
    marginRight: styles?.marginRight,
    paddingTop: styles?.paddingTop,
    paddingBottom: styles?.paddingBottom,
    paddingLeft: styles?.paddingLeft,
    paddingRight: styles?.paddingRight,
  }
})

const isVersionGreaterThanOne = computed(() => {
  return documentVersion.value > 1
})
</script>

<template>
  <div v-if="editor" class="w-full">
    <div :style="customStyleForTextParent" class="w-full box-border">
      <div
        class="el-wrapper table-el w-full"
        :class="{
          'document-version-gt-1': isVersionGreaterThanOne,
          'pointer-events': !isEditable,
        }"
        :key="refreshCount"
      >
        <RichTextTableContent
          :element="props.element"
          :custom-values="flatCustomValues"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.editor-wrapper {
  .tiptap.ProseMirror[contenteditable='false'] {
    white-space: break-spaces !important;
  }
}

.ProseMirror {
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 1px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: bold;
      text-align: left;
      background-color: #f1f3f5;
    }

    .selectedCell:after {
      z-index: 2;
      position: absolute;
      content: '';
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background: rgba(200, 200, 255, 0.4);
      pointer-events: none;
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: -2px;
      width: 4px;
      background-color: #adf;
      pointer-events: none;
    }

    p {
      margin: 0;
    }
  }
}

// Apply text-align-last styles only when element.version > 1
.document-version-gt-1 {
  .ProseMirror {
    table {
      font-weight: 400;
      text-rendering: optimizeLegibility;
      line-height: 1.5;
      font-size: 16px;
      word-break: break-word;
      white-space: pre-wrap;
      /* newer spec: preserves every space + breaks at them */
      white-space: break-spaces !important;
      font-variant-ligatures: none;

      p {
        margin: 0;
        &[style*='text-align: justify'] {
          text-align-last: left;
        }
        &[style*='text-align: left'] {
          text-align-last: left;
        }
        &[style*='text-align: right'] {
          text-align-last: right;
        }
        &[style*='text-align: center'] {
          text-align-last: center;
        }

        span {
          /* keep your pre-wrap; then explicitly restore “break at spaces” */
          white-space: pre-wrap;
          /* newer spec: preserves every space + breaks at them */
          white-space: break-spaces !important;
          white-space-collapse: preserve; /* Chrome’s way of collapsing normal spaces */
          text-wrap-mode: wrap; /* Chrome-only */
          -webkit-nbsp-mode: normal; /* Safari-style breakable NBSP for both engines */
          // display: inline-block;
          max-width: 100%;
          word-break: break-word;
          vertical-align: top;
          display: inline;
        }

        a {
          display: inline;
        }
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin: 0;
        span {
          /* keep your pre-wrap; then explicitly restore “break at spaces” */
          white-space: pre-wrap;
          /* newer spec: preserves every space + breaks at them */
          white-space: break-spaces !important;
          white-space-collapse: preserve; /* Chrome’s way of collapsing normal spaces */
          text-wrap-mode: wrap; /* Chrome-only */
          -webkit-nbsp-mode: normal; /* Safari-style breakable NBSP for both engines */
          // display: inline-block;
          max-width: 100%;
          word-break: break-word;
          vertical-align: top;
          display: inline;
        }

        a {
          display: inline;
        }
      }
    }
  }
}

.pointer-events {
  pointer-events: none;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
</style>
